import { useCallback, useEffect, useMemo, useState } from "react";

import cn from "classnames";
import { POKE_DATA } from "game/data/pokemon/data";
import { POKEMON_SKILLS } from "game/data/skills/POKEMON_SKILLS";
import { PokeType } from "types/pokemon/poke-type";

import { Alert } from "@/components/alert/alert";
import { PokemonSkillCard } from "@/components/pokemon/pokemon-skill-card";
import { PokemonSprite } from "@/components/pokemon/pokemon-sprite";
import { PokemonTypeBadge } from "@/components/pokemon/pokemon-type-badge";
import { Button } from "@/components/ui/buttons/button";
import { useBackAction } from "@/hooks/use-back-action";
import { useAuth } from "@/providers/auth-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { listInventoryPokemons } from "@/services/inventory/list-inventory-pokemons";
import { startSandboxBattle } from "@/services/training/start-sandbox-battle";
import { RouteNames } from "@/types/route-names";
import { calculatePurityScore, getPurityInfo } from "@/utils/purity";


import { DojoBattleView } from "./dojo-battle-view";

import type { PokeNames } from "types/pokemon/poke-names";
import type { InventoryPokemonsOutput } from "types/web-app";



type OwnedPokemon = InventoryPokemonsOutput["player_pokemons"][number];

export const DojoSandboxView = () => {
  const { previousRoute, setCurrentRoute } = useSPAContext();
  const { getAccessToken } = useAuth();

  const [step, setStep] = useState<1 | 2 | 3>(1);

  const [query, setQuery] = useState("");
  const [owned, setOwned] = useState<OwnedPokemon[] | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [selectedPokemonId, setSelectedPokemonId] = useState<number | null>(
    null,
  );
  const selectedPokemon = useMemo(
    () => owned?.find((p) => p.id === selectedPokemonId) ?? null,
    [owned, selectedPokemonId],
  );

  const selectedPokemonPurityInfo = useMemo(() => {
    if (!selectedPokemon) return null;
    const pokeData = POKE_DATA[selectedPokemon.base_pokemon_name as PokeNames];
    const purityScore = calculatePurityScore(
      selectedPokemon.talents ?? [],
      pokeData?.types ?? [],
    );
    return getPurityInfo(purityScore);
  }, [selectedPokemon]);

  const [selectedSkills, setSelectedSkills] = useState<string[]>([]);
  const [selectedDummyTypes, setSelectedDummyTypes] = useState<PokeType[]>([]);
  const [isStarting, setIsStarting] = useState(false);
  const [battleData, setBattleData] = useState<{
    battleLog: any;
    botName: string;
    botCountry: string | null;
  } | null>(null);

  const handleBack = useCallback(() => {
    if (battleData) {
      setBattleData(null);
      return;
    }
    if (step > 1) {
      setStep((prev) => (prev - 1) as 1 | 2 | 3);
      return;
    }
    setCurrentRoute(previousRoute ?? RouteNames.DOJO);
  }, [battleData, previousRoute, setCurrentRoute, step]);

  useBackAction(handleBack);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const token = getAccessToken();
      if (!token) return;
      setIsLoading(true);
      try {
        const res = await listInventoryPokemons(token);
        if (!cancelled) {
          setOwned(res.ok && res.data ? res.data.player_pokemons : []);
        }
      } catch (e) {
        console.error(e);
        if (!cancelled) setOwned([]);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [getAccessToken]);

  const filteredOwned = useMemo(() => {
    const list = owned ?? [];
    const q = query.trim().toLowerCase();

    if (!q) {
      return list;
    }

    return list.filter(
      (p) =>
        p.base_pokemon_name?.toLowerCase().includes(q) ||
        p.name?.toLowerCase().includes(q),
    );
  }, [owned, query]);

  const skillCards = useMemo(() => {
    if (!selectedPokemon) return [];
    const known = selectedPokemon.known_skills ?? [];
    return known
      .map((id) => id.replace(/_/g, "-"))
      .map((id) => ({
        id,
        data: POKEMON_SKILLS[id as keyof typeof POKEMON_SKILLS],
      }))
      .filter((x) => !!x.data);
  }, [selectedPokemon]);

  const toggleSkill = useCallback((skillId: string) => {
    setSelectedSkills((prev) => {
      const normalized = skillId.replace(/_/g, "-");
      const has = prev.includes(normalized);
      if (has) return prev.filter((x) => x !== normalized);
      if (prev.length >= 3) return prev;
      return [...prev, normalized];
    });
  }, []);

  const toggleDummyType = useCallback((type: PokeType) => {
    setSelectedDummyTypes((prev) => {
      if (prev.includes(type)) return prev.filter((t) => t !== type);
      if (prev.length >= 2) return prev;
      return [...prev, type];
    });
  }, []);

  const handleStart = useCallback(async () => {
    if (!selectedPokemonId) {
      Alert("Select a Pokémon first.");
      return;
    }
    if (selectedSkills.length !== 3) {
      Alert("Select exactly 3 skills.");
      return;
    }
    const token = getAccessToken();
    if (!token) {
      Alert("Authentication error. Please try again");
      return;
    }

    setIsStarting(true);
    try {
      const res = await startSandboxBattle(token, {
        pokemon_id: selectedPokemonId,
        active_skills: selectedSkills,
        dummy_types: selectedDummyTypes,
      });

      if (res.ok && res.data) {
        setBattleData({
          battleLog: res.data.battle_log,
          botName: res.data.bot_name,
          botCountry: res.data.bot_country,
        });
      } else {
        Alert(res.message || "Failed to start sandbox");
      }
    } catch (e) {
      console.error(e);
      Alert("Failed to start sandbox");
    } finally {
      setIsStarting(false);
    }
  }, [getAccessToken, selectedPokemonId, selectedSkills, selectedDummyTypes]);

  if (battleData) {
    return (
      <DojoBattleView
        battleLog={battleData.battleLog}
        botName={battleData.botName}
        botCountry={battleData.botCountry}
        onBack={handleBack}
        onRematch={handleStart}
      />
    );
  }

  return (
    <div className="relative flex flex-col w-full h-full max-h-full overflow-hidden">
      {/* Background Map */}
      <div
        className="z-0 absolute inset-0 bg-cover bg-no-repeat bg-center"
        style={{
          backgroundImage:
            "url('/assets/maps/training-dojo/training-dojo.png')",
        }}
      />

      {/* Overlay Content */}
      <div className="z-10 relative flex flex-col bg-pk-night-1/80 backdrop-blur-sm p-1 pb-24 w-full h-full overflow-y-auto">
        {/* Header */}
        <div className="relative flex justify-between items-center mt-6 xs:mt-8 px-2">
          <div className="flex flex-col">
            <div className="relative">
              <h1 className="drop-shadow-lg font-black text-white text-3xl xs:text-4xl uppercase leading-none tracking-tight">
                Dojo Training
              </h1>
              <div className="bg-pk-red-1 mt-1 w-12 xs:w-16 h-1 skew-x-[-20deg]" />
            </div>
          </div>
        </div>

        <div className="relative flex flex-col gap-3 px-1 pt-4 w-full max-h-full grow">
          {/* STEP 1: Pick Pokemon */}
          <div
            className={cn(
              "flex flex-col bg-black/40 backdrop-blur-md p-3 border border-white/10 rounded-xl transition-all duration-300",
              step === 1 ? "grow max-h-[60%]" : "shrink-0",
            )}
          >
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center gap-2">
                <div className="font-bold text-pk-gray-4 text-xs uppercase tracking-widest">
                  1. Select Pokémon
                </div>
                <div className="bg-white/10 px-1.5 py-0.5 border border-white/5 rounded font-bold text-[9px] text-pk-yellow-1 uppercase tracking-wide">
                  Normalized to Lv.50
                </div>
              </div>
              {step > 1 && (
                <Button
                  variant="night"
                  className="px-3 py-1 h-auto text-xs"
                  onClick={() => setStep(1)}
                >
                  Change
                </Button>
              )}
            </div>

            {step === 1 ? (
              <>
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search your Pokémon..."
                  className="bg-black/50 mb-2 px-3 py-2 border border-white/10 rounded-lg outline-none w-full text-white placeholder:text-white/30"
                />

                <div className="flex-1 gap-2 grid grid-cols-3 xs:grid-cols-4 p-1 min-h-0 overflow-visible overflow-y-auto">
                  {isLoading && (
                    <div className="col-span-3 xs:col-span-4 py-2 text-pk-gray-4 text-sm text-center italic">
                      Loading your Pokémon...
                    </div>
                  )}
                  {!isLoading &&
                    filteredOwned.map((p) => {
                      const isSelected = p.id === selectedPokemonId;
                      const pokeData =
                        POKE_DATA[p.base_pokemon_name as PokeNames];
                      const purity = calculatePurityScore(
                        p.talents ?? [],
                        pokeData?.types ?? [],
                      );
                      const rank = getPurityInfo(purity).rank;

                      return (
                        <button
                          key={p.id}
                          onClick={() => {
                            setSelectedPokemonId(p.id);
                            setSelectedSkills(
                              (p.active_skills ?? [])
                                .map((x) => x.replace(/_/g, "-"))
                                .slice(0, 3),
                            );
                            setStep(2);
                          }}
                          className={`relative rounded-xl border h-32 px-2 py-3 text-left text-white text-sm overflow-hidden transition-all ${
                            isSelected
                              ? "bg-pk-night-2 ring-2 ring-pk-yellow-1 border-white/10 shadow-lg"
                              : "bg-pk-night-2/40 hover:bg-pk-night-2 border-white/5 hover:border-white/20 hover:scale-105"
                          }`}
                        >
                          <div className="flex flex-col items-center gap-1.5">
                            <div className="relative flex justify-center items-center bg-black/20 rounded-lg w-7 h-7">
                              <PokemonSprite
                                pokemon={p}
                                className="w-12 h-12"
                                style={{ scale: 1.5 }}
                                ignoreScale={true}
                              />
                            </div>

                            <div className="flex flex-col items-center gap-0.5 w-full">
                              <div className="w-full font-bold text-[11px] text-white text-center truncate leading-tight">
                                {p.name || p.base_pokemon_name}
                              </div>
                              {p.name && (
                                <div className="w-full text-[9px] text-pk-gray-4 text-center truncate uppercase tracking-wide">
                                  {p.base_pokemon_name}
                                </div>
                              )}
                              <div className="flex justify-center items-center gap-2 bg-white/5 mt-1 py-0.5 rounded-full w-full text-[10px]">
                                <span className="font-bold text-pk-yellow-1">
                                  Lv.{p.level}
                                </span>
                                <span className="font-medium text-white/30">
                                  #{p.display_id}
                                </span>
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
              </>
            ) : (
              selectedPokemon && (
                <div className="flex items-center gap-3">
                  <div className="flex justify-center items-center bg-pk-night-2/80 rounded-lg w-10 h-10">
                    <PokemonSprite
                      pokemon={selectedPokemon}
                      className="w-8 h-8"
                      style={{ scale: 1.4 }}
                      ignoreScale={true}
                    />
                  </div>
                  <div className="flex flex-col">
                    <div className="font-bold text-white text-sm">
                      {selectedPokemon.name ?? `#${selectedPokemon.display_id}`}
                    </div>
                    <div className="text-pk-gray-4 text-xs">
                      Lv. {selectedPokemon.level} • Tal.{" "}
                      {selectedPokemonPurityInfo?.rank ?? "?"}
                    </div>
                  </div>
                </div>
              )
            )}
          </div>

          {/* STEP 2: Pick Skills */}
          {selectedPokemon && (
            <div
              className={cn(
                "flex flex-col bg-black/40 backdrop-blur-md p-3 border border-white/10 rounded-xl transition-all duration-300",
                step === 2 ? "grow max-h-[60%]" : "shrink-0",
              )}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <div className="font-bold text-pk-gray-4 text-xs uppercase tracking-widest">
                    2. Select Skills
                  </div>
                  {step === 2 && (
                    <div className="text-pk-gray-4 text-xs">
                      {selectedSkills.length}/3
                    </div>
                  )}
                </div>
                {step > 2 && (
                  <Button
                    variant="night"
                    className="px-3 py-1 h-auto text-xs"
                    onClick={() => setStep(2)}
                  >
                    Change
                  </Button>
                )}
              </div>

              {step === 2 ? (
                <>
                  <div className="flex-1 gap-2 grid grid-cols-1 xs:grid-cols-2 min-h-0 overflow-x-hidden overflow-y-auto">
                    {skillCards
                      .filter((s) => !!s.data)
                      .map((s) => (
                        <PokemonSkillCard
                          key={s.id}
                          skill={s.data!}
                          isSelected={selectedSkills.includes(s.id)}
                          onClick={() => toggleSkill(s.id)}
                          isSelectable={true}
                          compact
                        />
                      ))}
                  </div>
                  <div className="mt-3">
                    <Button
                      variant={
                        selectedSkills.length === 3 ? "default" : "night"
                      }
                      disabled={selectedSkills.length !== 3}
                      className="w-full"
                      onClick={() => setStep(3)}
                    >
                      Next
                    </Button>
                  </div>
                </>
              ) : (
                <div className="flex flex-wrap gap-2">
                  {selectedSkills.map((id) => {
                    const skill =
                      POKEMON_SKILLS[id as keyof typeof POKEMON_SKILLS];
                    if (!skill) return null;
                    const typeName = skill.pokemon_type.toLowerCase();
                    const typeColors: Record<string, string> = {
                      fire: "bg-radial from-pk-red-5 via-pk-fire to-pk-fire",
                      water: "bg-radial from-pk-water to-pk-blue-5",
                      grass: "bg-radial from-pk-grass to-pk-green-4",
                      electric: "bg-radial from-pk-electric to-pk-yellow-3",
                      ice: "bg-radial from-pk-ice to-pk-blue-4",
                      fighting: "bg-radial from-pk-fighting to-pk-red-4",
                      poison: "bg-radial from-pk-poison to-pk-purple-4",
                      ground: "bg-radial from-pk-beige-3 to-pk-ground",
                      flying: "bg-radial from-pk-flying to-pk-blue-5",
                      psychic: "bg-radial from-pk-psychic to-pk-garnet-4",
                      bug: "bg-radial from-pk-bug to-pk-green-5",
                      rock: "bg-radial from-pk-rock to-pk-gray-4",
                      dragon: "bg-radial from-pk-dragon to-pk-purple-4",
                      steel: "bg-radial from-pk-steel to-pk-blue-4",
                      fairy: "bg-radial from-pk-fairy to-pk-garnet-4",
                      dark: "bg-radial from-pk-dark to-pk-gray-2",
                      ghost: "bg-radial from-pk-ghost to-pk-pink-4",
                      normal: "bg-radial from-pk-normal to-pk-gray-5",
                    };
                    const bgColor = typeColors[typeName] || "bg-pk-gray-2";
                    return (
                      <div
                        key={id}
                        className={cn(
                          "relative flex items-center gap-1.5 rounded-lg",
                          "border-2 border-white/40",
                          "border-b-4 border-b-black/20",
                          "shadow-[inset_0_1px_0_rgba(255,255,255,0.4)]",
                          "px-2 py-1",
                          bgColor,
                        )}
                      >
                        <div className="absolute inset-0 bg-gradient-to-b from-white/10 to-transparent rounded-lg pointer-events-none" />
                        <img
                          src={`/assets/pokemon-types/${typeName}.svg`}
                          className="z-10 relative drop-shadow-sm w-3 h-3"
                          alt={skill.pokemon_type}
                        />
                        <span className="z-10 relative drop-shadow-sm font-bold text-white text-xs">
                          {skill.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* STEP 3: Pick Dummy Types */}
          {selectedPokemon && selectedSkills.length === 3 && (
            <div
              className={cn(
                "flex flex-col bg-black/40 backdrop-blur-md p-3 border border-white/10 rounded-xl transition-all duration-300",
                step === 3 ? "grow" : "shrink-0",
              )}
            >
              <div className="flex justify-between items-center mb-2">
                <div className="flex items-center gap-2">
                  <div className="font-bold text-pk-gray-4 text-xs uppercase tracking-widest">
                    3. Dummy Types
                  </div>
                  {step === 3 && (
                    <div className="text-pk-gray-4 text-xs">
                      {selectedDummyTypes.length}/2
                    </div>
                  )}
                </div>
              </div>

              {step === 3 ? (
                <>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {Object.values(PokeType).map((type) => {
                      const isSelected = selectedDummyTypes.includes(type);
                      return (
                        <div
                          key={type}
                          onClick={() => toggleDummyType(type)}
                          className={cn(
                            "hover:scale-105 active:scale-95 transition-all cursor-pointer",
                            !isSelected &&
                              "opacity-50 grayscale hover:grayscale-0 hover:opacity-100",
                          )}
                        >
                          <PokemonTypeBadge
                            type={type}
                            hideText
                            iconSize={18}
                            className="px-2 py-1"
                          />
                        </div>
                      );
                    })}
                  </div>

                  <Button
                    variant="red"
                    disabled={isStarting}
                    className="shadow-xl mt-auto py-4 w-full"
                    onClick={handleStart}
                  >
                    <span className="font-black tracking-wider">
                      {isStarting ? "STARTING..." : "START BATTLE"}
                    </span>
                  </Button>
                </>
              ) : (
                <div className="flex gap-1">
                  {selectedDummyTypes.length > 0 ? (
                    selectedDummyTypes.map((type) => (
                      <PokemonTypeBadge
                        key={type}
                        type={type}
                        hideText
                        iconSize={16}
                        className="px-2 py-0.5"
                      />
                    ))
                  ) : (
                    <span className="text-pk-gray-4 text-xs italic">
                      None (Normal)
                    </span>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
